import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";
import FamilyAuthLayout, {
  FIELD,
  FormError,
  HEADING,
  LEAD,
  QUIET_LINK,
} from "../../components/family/FamilyAuthLayout";

const API = process.env.REACT_APP_API_URL;

// Localhost and the dev.* frontend use the development mobile app. All other
// frontend hosts use the production app, so no separate environment variable is
// needed for the deep-link scheme.
const FRONTEND_HOST =
  typeof window === "undefined" ? "" : window.location.hostname.toLowerCase();
const IS_DEVELOPMENT_FRONTEND =
  FRONTEND_HOST === "localhost" ||
  FRONTEND_HOST === "127.0.0.1" ||
  FRONTEND_HOST.startsWith("dev.");
const APP_SCHEME = IS_DEVELOPMENT_FRONTEND ? "myapp-dev" : "myapp";

const STORES = {
  ios: {
    label: "App Store",
    Icon: FaApple,
    href: process.env.REACT_APP_IOS_APP_STORE_URL || "https://apps.apple.com/de/search?term=SteffiCare",
  },
  android: {
    label: "Google Play",
    Icon: FaGooglePlay,
    href:
      process.env.REACT_APP_ANDROID_PLAY_STORE_URL ||
      "https://play.google.com/store/search?q=SteffiCare&c=apps",
  },
};

function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return "ios";
  return "desktop";
}

function StoreButton({ store }) {
  const { label, Icon, href } = STORES[store];
  return (
    <a href={href} target="_blank" rel="noreferrer" className="btn btn-ghost w-full justify-center">
      <Icon aria-hidden className="h-[1.1em] w-[1.1em]" /> {label}
    </a>
  );
}

/* Hands the invitation over to the SteffiCare app. Signing up in the browser
   is kept as a quiet fallback for people who cannot install the app. */
export default function FamilyInviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const platform = useMemo(() => detectPlatform(), []);
  const isMobile = platform !== "desktop";
  const appHref = `${APP_SCHEME}://family/invite/${token}`;

  const [status, setStatus] = useState("loading"); // loading | ready | invalid | error
  const [invite, setInvite] = useState(null);
  const [view, setView] = useState("app"); // app | browser
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    relationship: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    (async () => {
      try {
        const res = await fetch(`${API}/family-invites/redeem/${token}`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok) {
          setInvite(data);
          setStatus("ready");
        } else {
          setStatus(res.status === 404 ? "invalid" : "error");
        }
      } catch {
        if (alive) setStatus("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // On a phone, try the app straight away; the buttons stay as the fallback.
  useEffect(() => {
    if (status === "ready" && isMobile) window.location.assign(appHref);
  }, [appHref, isMobile, status]);

  function setField(field) {
    return (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/family-invites/redeem/${token}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          password: form.password,
          relationship_label: form.relationship,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Das Konto konnte nicht erstellt werden.");
      navigate("/family", { replace: true });
    } catch (error) {
      setErr(error?.message || "Das Konto konnte nicht erstellt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  const patientName =
    [invite?.patient?.forename, invite?.patient?.surname].filter(Boolean).join(" ") || "deine Liebsten";

  let content;
  if (status === "loading") {
    content = <p className="text-lg text-[color:var(--ink-3)]">Einladung wird geprüft …</p>;
  } else if (status === "invalid" || status === "error") {
    const invalid = status === "invalid";
    content = (
      <>
        <h1 className={HEADING}>
          {invalid ? "Dieser Link gilt nicht mehr" : "Die Einladung lädt gerade nicht"}
        </h1>
        <p className={LEAD}>
          {invalid
            ? "Bitte frag nach einer neuen Einladung."
            : "Bitte versuch es in ein paar Minuten noch einmal."}
        </p>
        <div className="mt-8">
          <Link to="/family/login" className={QUIET_LINK}>
            Bereits registriert? Anmelden
          </Link>
        </div>
      </>
    );
  } else if (view === "app") {
    content = (
      <>
        <h1 className={HEADING}>Du bist eingeladen</h1>
        <p className={LEAD}>
          Erstelle dein Konto in der SteffiCare-App und begleite{" "}
          <span className="font-semibold text-[color:var(--ink)]">{patientName}</span>.
        </p>

        <div className="mt-8 flex flex-col gap-3">
          {isMobile ? (
            <>
              <a href={appHref} className="btn btn-primary w-full justify-center">
                In der App öffnen
              </a>
              <StoreButton store={platform} />
            </>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <StoreButton store="ios" />
              <StoreButton store="android" />
            </div>
          )}
        </div>
        <p className="mt-4 text-base leading-snug text-[color:var(--ink-3)]">
          {isMobile
            ? "Nach der Installation diesen Link noch einmal öffnen."
            : "Danach diesen Link auf dem Handy öffnen."}
        </p>

        <div className="mt-8">
          <button type="button" onClick={() => setView("browser")} className={QUIET_LINK}>
            Im Browser registrieren
          </button>
        </div>
      </>
    );
  } else {
    content = (
      <>
        <button
          type="button"
          onClick={() => {
            setErr("");
            setView("app");
          }}
          aria-label="Zurück"
          className="absolute left-4 top-4 grid h-11 w-11 place-items-center rounded-full text-[color:var(--ink-3)] transition hover:bg-[color:var(--bg-2)] hover:text-[color:var(--ink)]"
        >
          <FiArrowLeft className="h-[1.375rem] w-[1.375rem]" aria-hidden />
        </button>
        <h1 className={HEADING}>Du bist eingeladen</h1>
        <p className={LEAD}>
          Dein Konto wird mit{" "}
          <span className="font-semibold text-[color:var(--ink)]">{patientName}</span> verknüpft.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-3 text-left">
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              required
              value={form.firstName}
              onChange={setField("firstName")}
              placeholder="Vorname"
              autoComplete="given-name"
              className={FIELD}
            />
            <input
              required
              value={form.lastName}
              onChange={setField("lastName")}
              placeholder="Nachname"
              autoComplete="family-name"
              className={FIELD}
            />
          </div>
          <input
            required
            type="email"
            value={form.email}
            onChange={setField("email")}
            placeholder="E-Mail-Adresse"
            autoComplete="email"
            className={FIELD}
          />
          <input
            required
            type="password"
            minLength={8}
            value={form.password}
            onChange={setField("password")}
            placeholder="Passwort, mind. 8 Zeichen"
            autoComplete="new-password"
            className={FIELD}
          />
          <input
            value={form.relationship}
            onChange={setField("relationship")}
            placeholder="Beziehung, z. B. Tochter (optional)"
            className={FIELD}
          />

          {err ? <FormError>{err}</FormError> : null}

          <button type="submit" disabled={submitting} className="btn btn-primary mt-2 w-full justify-center">
            {submitting ? "Konto wird erstellt …" : "Konto erstellen"}
          </button>
        </form>

        <div className="mt-8">
          <Link to="/family/login" className={QUIET_LINK}>
            Bereits registriert? Anmelden
          </Link>
        </div>
      </>
    );
  }

  return <FamilyAuthLayout documentTitle="Einladung · SteffiCare">{content}</FamilyAuthLayout>;
}
